import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";

import { makeStyles, Grid, Typography } from "@material-ui/core";

import { PAGES } from "../../";
import { GRAPH_UNITS, KPIS } from "../constants";
import { Toggle } from "../../../components/toggle";
import { PlusButton } from "../../../components/base";
import { BarChart } from "../../../components/charts";
import { ColorIndicator } from "../../../components/colorIndicator";
import { BAR_CHART_COLORS, CHART_KEYS } from "../constants";
import {
  api,
  nFormatter,
  getPercent,
  getLastBarTotal,
  currencyFormatter,
  requestDateFormat,
  generateEstimatedData,
  isResponseDataPerMonth,
} from "../../../services";
import { COLORS } from "../../../theme/colors";
import useDiff from "../../../hooks/useDiff";
import { NoDataCard } from "../../../components/parts";

const useStyles = makeStyles((theme) => ({
  container: {
    height: "80%",
    paddingLeft: theme.spacing(4),
  },
  row: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    "& p": {
      fontSize: 12,
    },
  },
  title: {
    marginBottom: theme.spacing(1.5),
  },
}));

const co2Bars = [
  {
    dataKey: "trading",
    fill: COLORS.purple,
    unit: "tCO2",
  },
  {
    dataKey: "battery",
    fill: "#4F31CF",
    unit: "tCO2",
  },
  {
    dataKey: "ev",
    fill: COLORS.cian,
    unit: "tCO2",
  },
];

const dollarBars = [
  {
    dataKey: "allowances_trading",
    fill: COLORS.purple,
  },
  {
    dataKey: "voluntery_credits",
    fill: "#4F31CF",
  },
  {
    dataKey: "offset_credits",
    fill: COLORS.cian,
  },
];

export const chartLegends = [
  {
    key: "trading",
    text: "Trading",
    unit: "tCO2",
  },
  {
    key: "battery",
    text: "Battery",
    unit: "tCO2",
  },
  {
    key: "ev",
    text: "EV",
    unit: "tCO2",
  },
  {
    key: "allowances_trading",
    text: "Allowances trading",
  },
  {
    key: "voluntery_credits",
    text: "Voluntery credits",
  },
  {
    key: "offset_credits",
    text: "Offset credits",
  },
];

export const EmissionReductionChart = ({ activeKpi, period, startDate, endDate }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [graphUnit, setGraphUnit] = useState(GRAPH_UNITS.tonCarbon);
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [legends, setLegends] = useState([]);
  const [diffDays] = useDiff(startDate, endDate);
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);

  const graphBars = useMemo(() => {
    if (graphUnit === GRAPH_UNITS.tonCarbon) return co2Bars;

    return dollarBars;
  }, [graphUnit]);

  const handleClick = () => {
    navigate(`${PAGES.dashboard.index}/${PAGES.dashboard.carbonImpact}`);
  };

  useEffect(() => {
    const legends = chartLegends.map((legend) => ({
      ...legend,
      value: 0,
      percentage: 0,
    }));

    const isDataPerMonth = isResponseDataPerMonth(rawData);
    const currentYear = new Date().getUTCFullYear();

    const data = rawData.map((item) => {
      const date = new Date(item.date);
      const year = date.getUTCFullYear();

      for (const legend of legends) {
        legend.value += item[legend.key];
      }
      if (year > currentYear) {
        const estimated =
          graphUnit === GRAPH_UNITS.tonCarbon
            ? item.trading + item.battery + item.ev
            : item.allowances_trading + item.offset_credits + item.voluntery_credits;
        return {
          estimated,
          x: `${isDataPerMonth ? `${date.getMonth() + 1}-` : ""}${date.getUTCFullYear()}-E*`,
        };
      }
      return {
        ...item,
        x: `${isDataPerMonth ? `${date.getMonth() + 1}-` : ""}${date.getUTCFullYear()}`,
      };
    });

    const total = legends.reduce((prev, current) => prev + current.value, 0);
    legends.forEach((legend) => {
      legend.percentage = getPercent(legend.value, total);
    });

    const fields =
      graphUnit === GRAPH_UNITS.tonCarbon
        ? ["trading", "battery", "ev"]
        : ["allowances_trading", "voluntery_credits", "offset_credits"];
    const lastBarTotal = getLastBarTotal(data, fields);
    const estimatedData = generateEstimatedData(data, lastBarTotal, isDataPerMonth, undefined);
    setLegends(legends);
    setData(data.concat(estimatedData));
  }, [rawData, graphUnit]);

  useEffect(() => {
    if (activeKpi === KPIS.emission_reduction) {
      const getData = (url) => {
        api.get(url).then((resp) => setRawData(resp.data));
      };

      let url = "/praas/v1/dashboard/emission-reduction-graph/";
      if (period.name === "RANGE" && endDate) {
        url += `?from_date=${requestDateFormat(startDate)}&to_date=${requestDateFormat(endDate)}`;
        getData(url);
      } else if (["YTD", "Acc.", "20Y"].includes(period.name)) {
        url += `?date_filter=${period.filter}`;
        getData(url);
      }
    }
  }, [activeKpi, period, startDate, endDate]);

  return (
    <>
      <Grid className={classes.title} container alignItems="center">
        <Typography
          style={{
            fontWeight: "bold",
            marginRight: 25,
          }}
        >
          Carbon emission reduction
        </Typography>
        <Toggle
          value={graphUnit}
          options={[currencySymbol, GRAPH_UNITS.tonCarbon]}
          onChanged={(unit) => {
            setGraphUnit(unit);
          }}
        />
      </Grid>
      {rawData.length ? (
        <Grid container>
          <Grid item xs={9} xl={10}>
            <BarChart chartData={data} bars={graphBars} period={period} dateDiff={diffDays} />
          </Grid>
          <Grid item xs={3} xl={2}>
            <Grid
              className={classes.container}
              container
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs={10}>
                {legends
                  .filter(
                    (l) => l.unit === graphUnit || (!l.unit && graphUnit !== GRAPH_UNITS.tonCarbon)
                  )
                  .map((legend, index) => (
                    <Grid
                      className={classes.row}
                      key={index}
                      container
                      alignItems="center"
                      justifyContent="space-evenly"
                    >
                      <Grid item xs={legend.unit ? 5 : 6}>
                        <Grid container alignItems="center">
                          <ColorIndicator
                            color={BAR_CHART_COLORS[legend.key]}
                            onlyBorder={legend.key === CHART_KEYS.total_unrealized}
                          />
                          <Typography>{legend.text}</Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={legend.unit ? 4 : 3}>
                        <Typography>
                          {legend.unit
                            ? `${nFormatter(legend.value)} ${legend.unit}`
                            : currencyFormatter(legend.value, currencySymbol)}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography>{legend.percentage}%</Typography>
                      </Grid>
                    </Grid>
                  ))}
              </Grid>
              <Grid item xs={2}>
                <PlusButton onClick={handleClick} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid>
          <NoDataCard rootStyle={{ minHeight: 200 }} gridStyle={{ height: 120 }} />
        </Grid>
      )}
    </>
  );
};
